<template>
  <div id="metadatacontainer">
    <h1>Revisions</h1>
    <div id="formatList">
      <div
        v-for="revision in revisions"
        :key="revision.id"
        class="formatTile hologram"
        :class="{
          'selected-hologram': isSelectedRevision(revision),
          'selected-revision': currentlyLoadedRevisionId.value === revision.id,
        }"
      >
        <div
          class="revision-verified-box"
          :class="{
            checked: revision.isVerified,
          }"
        ></div>
        <div class="select-revision-zone" @click="selectRevision(revision)">
          <div v-if="isSelectedRevision(revision)" class="active-revision">
            Active
          </div>
          <div
            class="tile-date"
            :class="{
              selected: isSelectedRevision(revision),
            }"
          >
            {{ displayText(revision) }}
          </div>
          <div>
            {{ revision.metadata._author || "Author unknown" }}
          </div>
        </div>
        <div>
          <i
            class="fa"
            :class="{
              disabled: isReadOnlyGroup,
              'fa-lock': revision.isLocked,
              'fa-lock-open': !revision.isLocked,
            }"
            @click="toggleLock(revision)"
          ></i>
          <i
            class="fa fa-ellipsis-v ellipsis"
            @click="showEllipsisMenu(revision, $event)"
          >
          </i>
        </div>
      </div>
    </div>
    <div id="ellipsis-menu">
      <div
        v-if="selectedEllipsisMenu"
        v-click-outside="onFocusOut"
        class="ellipsis-menu"
      >
        <ul>
          <li @click="setRevisionActive(selectedEllipsisMenu)">Set Active</li>
          <li
            :class="{ disabled: isReadOnlyGroup }"
            @click="clickDownload(selectedEllipsisMenu)"
          >
            Download Data File
          </li>
          <li
            :class="{ disabled: isReadOnlyGroup }"
            @click="clickDelete(selectedEllipsisMenu)"
          >
            Delete
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import featureToggles from "../enums/FeatureToggles.js";
import { mapState, mapGetters, mapActions } from "vuex";
import { bus } from "../eventBus.js";

export default {
  name: "AppHologramRevisions",
  data: () => ({
    featureToggles: featureToggles,
    selectedRevision: { value: null },
    selectedEllipsisMenu: null,
    currentlyLoadedRevisionId: { value: null },
  }),
  computed: {
    ...mapState([
      "selectedGroup",
      "selectedAsset",
      "allowedFeatureToggles",
      "isMaximized",
    ]),
    ...mapGetters(["isReadOnlyGroup"]),
    revisions() {
      if (!this.selectedAsset) {
        return [];
      }
      const revisions = [...this.selectedAsset.revisions];
      revisions.sort((a, b) => new Date(b._created) - new Date(a._created));
      return revisions;
    },
  },
  watch: {
    "$store.state.isMaximized": function () {
      if (this.isMaximized) {
        this.selectedRevision.value = null;
      }
    },
    "$store.state.selectedAsset": function () {
      this.selectedRevision.value = null;
    },
  },
  created() {
    bus.$on("variable.add.SceneRefresher.selectedFormatId", (eventVariable) => {
      this.currentlyLoadedRevisionId = eventVariable;
    });

    bus.$on(
      "variable.add.HologramViewModel.selectedRevision",
      (eventVariable) => {
        const currentRevisionId = this.selectedRevision.value;
        this.selectedRevision = eventVariable;
        this.selectedRevision.value = currentRevisionId;
      }
    );
  },
  methods: {
    ...mapActions(["getAzure", "postAzure"]),
    onFocusOut(event) {
      if (event.srcElement.classList.contains("ellipsis")) return;
      this.selectedEllipsisMenu = null;
    },
    clickDelete(format) {
      this.$emit("deleteformat", format);
      this.selectedEllipsisMenu = null;
    },
    clickDownload(format) {
      this.$emit("downloadformat", format);
      this.selectedEllipsisMenu = null;
    },
    clickExplore(format) {
      this.$emit("exploreformat", format);
    },
    toggleLock(revision) {
      this.$emit("togglelock", revision);
    },
    displayText(revision) {
      const postfix = revision.metadata.displayName
        ? ` (${revision.metadata.displayName})`
        : "";
      const modifiedTime = this.localTime(revision._created);
      return modifiedTime + postfix;
    },
    isSelectedRevision(revision) {
      const revisions = this.revisions;
      if (revisions.some((x) => x.isSelected)) {
        if (revision.isSelected) {
          this.selectedRevision.value = revision.id;
        }
        return revision.isSelected;
      }
      return this.revisions[0].id === revision.id;
    },
    setRevisionActive(revision) {
      this.$emit("setactive", revision);
      this.selectedEllipsisMenu = null;
      this.selectRevision(revision);
    },
    selectRevision(revision) {
      const groupId = this.selectedGroup.id;
      const hologramId = this.selectedAsset.id;
      bus.$emit("unityUpdateEvent", {
        eventType: "AssetEvent.FormatSelected",
        data: JSON.stringify({
          groupId: groupId,
          assetId: hologramId,
          formatId: revision.id,
        }),
      });
      this.selectedRevision.value = revision.id;
    },
    localTime(date) {
      return date && moment(moment.utc(date).toDate()).format("lll");
    },
    showEllipsisMenu(format, event) {
      const ellipsisMenu = document.getElementById("ellipsis-menu");
      const offsetFromTopOfEllipsis = 30;
      ellipsisMenu.style.top =
        event.srcElement.offsetTop + offsetFromTopOfEllipsis + "px";
      ellipsisMenu.style.left =
        event.srcElement.offsetLeft +
        event.srcElement.clientWidth -
        ellipsisMenu.clientWidth +
        "px";

      this.selectedEllipsisMenu =
        this.selectedEllipsisMenu?.id === format.id ? null : format;
    },
  },
};
</script>

<style scoped>
#metadatacontainer {
  background-color: #2d6b7d;
  box-shadow: 3px 3px 20px black;
  border-radius: 10px;
  color: white;
  display: flex;
  margin: 20px 10px;
  align-items: center;
  flex-direction: column;
}
#metadatacontainer h1 {
  font-weight: bold;
}

ul.md-list.md-theme-default {
  background-color: transparent !important;
}

#formatList {
  background: linear-gradient(to left bottom, #5daec7, #3989a1);
  align-self: stretch;
  border-radius: 5px;
  background-color: transparent;
  padding: 7px 12px;
}

#formatList span {
  color: white;
  font-size: 18px;
}

#formatList i {
  padding: 7px;
  color: white;
}

.formatTile {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
  padding: 10px 8px;
}
.disabled {
  opacity: 0.5;
}
.hologram {
  opacity: 0.6;
}
.hologram.selected-hologram {
  background-color: #32a681;
  opacity: 1;
}
.hologram .selected {
  color: white;
  font-weight: bold;
  opacity: 1;
}
.fa {
  cursor: pointer;
}

.tile-date {
  font-size: 16px;
  padding-top: 4px;
  padding-bottom: 4px;
}

.revision-verified-box {
  width: 24px;
  height: 24px;
  background-size: 24px 24px;
  background-image: url("/public/assets/image/checkmark_unchecked.png");
  background-repeat: no-repeat;
}

.select-revision-zone {
  cursor: pointer;
  width: 180px;
}

.revision-verified-box.checked {
  background-image: url("/public/assets/image/checkmark_checked.png");
}

.selected-revision {
  background-color: #2d6b7d;
}

#ellipsis-menu {
  position: absolute;
  width: 176px;
}

.ellipsis-menu {
  border-radius: 5px;
  background-color: #204a57;
  padding: 2px;
}

.ellipsis-menu ul {
  list-style: none;
  padding: 0;
  margin: 0;
  margin-left: 19px;
}

.ellipsis-menu li {
  margin-top: 10px;
  margin-bottom: 10px;
  cursor: pointer;
}
</style>
